"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import {
  sectionHref,
  useAllowedSections,
} from "@/components/Layouts/pageSections";
import { ISectionNavItem, SectionSideNav } from "./SectionSideNav";

// A case or task opened from an inbox keeps the inbox's sidenav, with the
// section it belongs to selected.
const DETAIL_PAGE_SECTION: Record<string, string> = {
  cases: "inbox",
  tasks: "tasks",
};

export const InboxSideNav = ({ inboxId }: { inboxId: string }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { spotGroup } = useSpotUser();
  const sections = useAllowedSections("inbox");

  const { data: inbox } = useRemoteData("INBOXES_ID", {
    path: { group_id: spotGroup.id, inbox_id: inboxId },
  });

  const path = `/inbox/${inboxId}`;
  const isInboxPage = pathname === path;
  // A section the user may not open falls back to the first, as the page does.
  const tab = searchParams?.get("tab");
  const activeSection = isInboxPage
    ? (sections.find((section) => section.id === tab) ?? sections[0])?.id
    : DETAIL_PAGE_SECTION[pathname?.split("/")[1] ?? ""];

  // On the inbox page itself, switching section keeps the list filters.
  const hrefFor = (section: string) => {
    if (!isInboxPage) return sectionHref(path, section);
    const params = new URLSearchParams(searchParams?.toString());
    params.set("tab", section);
    return `${path}?${params.toString()}`;
  };

  const items: ISectionNavItem[] = sections.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
    isActive: section.id === activeSection,
    href: hrefFor(section.id),
  }));

  return (
    <SectionSideNav
      title={inbox?.name ?? "Inbox"}
      onBack={() => router.push("/")}
      testIdPrefix="inbox"
      groups={[{ items }]}
    />
  );
};
